import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';

// Task 35 — Faz A'nın tek örtük havuzu (`/v1/talent-pool` düz liste) yerini gerçek
// çoklu statik/dinamik havuz kavramına bıraktı (Task 21 `pools` alt-uçları). Bu hook
// artık HAVUZLARIN KENDİSİNİ listeler (üyeleri değil) — üyeler PoolDetailPage'in
// Üyeler sekmesinde ayrı bir uçtan (GetPoolMembersAsync) gelir.

export const NINE_BOX_CELL_KEYS = [
  'attention_needed', 'reliable_performer', 'expert_master',
  'developing', 'core_employee', 'high_performer',
  'hidden_talent', 'promising', 'star_hipo',
];

const useTalentPoolsApi = () => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  // TD-23 — bkz. utils/apiResult.js: GET okumaları reject etmez, sinyal res.data.status.
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/talent-pool/pools')
      .then((res) => {
        const { ok, items } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const addPool = useCallback(async (payload) => {
    try {
      await api.post('/v1/talent-pool/pools', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const updatePool = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/talent-pool/pools/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const removePool = useCallback(async (id) => {
    try {
      await api.post(`/v1/talent-pool/pools/${id}/remove`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const poolTypeOptions = useMemo(() => [
    { id: 'static', name: t('performance.talentpool.poolType.static') },
    { id: 'dynamic', name: t('performance.talentpool.poolType.dynamic') },
  ], [t]);

  const cellKeyOptions = useMemo(() => NINE_BOX_CELL_KEYS.map((k) => ({
    id: k,
    name: t(`performance.ninebox.cell.${k}`, { defaultValue: k }),
  })), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'ruleCellKeys', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'ruleFilterJson', label: ' ', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('performance.talentpool.poolName'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'poolType',
      label: t('performance.talentpool.poolTypeLabel'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const opt = poolTypeOptions.find((o) => o.id === v);
          return opt ? opt.name : v;
        },
      },
    },
    {
      name: 'memberCount',
      label: t('performance.talentpool.memberCount'),
      options: { filter: false, sort: false },
    },
  ], [t, poolTypeOptions]);

  return {
    fetching,
    data,
    loadFailed,
    columns,
    refresh,
    addPool,
    updatePool,
    removePool,
    poolTypeOptions,
    cellKeyOptions,
  };
};

export default useTalentPoolsApi;
